/**
 * La scheda di un contatto sul telefono (7/10/2026): risponde a "chi è e come
 * lo raggiungo?", come la scheda di un contatto dell'iPhone.
 *
 * Prima, a 390px: il nome in maiuscolo da 44px, quattro riquadri di numeri
 * (spesso a € 0, "niente aperto"), poi EMAIL / TELEFONO / SEDE con "Non ce
 * l'abbiamo". Qui:
 *   1. iniziali, nome, cos'è e dove sta;
 *   2. i gesti: chiama, scrivi, WhatsApp, mappa (spenti se il dato manca);
 *   3. i soldi, solo le righe che hanno un numero;
 *   4. le persone, le trattative aperte, i progetti, le ultime fatture;
 *   5. la scheda estesa del cliente, per tutto il resto.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { fmtDate, fmtEur } from '../../lib/utils';
import { MActions, MEmpty, MGroup, MIdentity, MPage, MRow, iniziali, leggibile } from '../../components/mobile/MobileKit';
import { TIPO_LABEL, canWriteContatti, contattoQuery } from './contactsApi';

/** "333 123 4567" → "393331234567" per WhatsApp; i fissi (che cominciano con 0) non ce l'hanno. */
function numeroWhatsApp(tel: string | null | undefined): string | null {
  if (!tel) return null;
  let n = tel.replace(/[^\d+]/g, '');
  if (n.startsWith('+')) n = n.slice(1);
  else if (n.startsWith('00')) n = n.slice(2);
  else if (n.startsWith('3') && n.length === 10) n = `39${n}`;
  if (n.startsWith('390')) return null;
  return n.length >= 10 ? n : null;
}

/** "+393280663526" → "+39 328 066 3526": scritto tutto attaccato non si legge. */
function telefonoLeggibile(tel: string): string {
  const n = tel.replace(/[^\d+]/g, '');
  const m = n.match(/^(\+39)?(3\d{2})(\d{3})(\d{3,4})$/);
  if (m) return [m[1], m[2], m[3], m[4]].filter(Boolean).join(' ');
  const f = n.match(/^(\+39)?(0\d{1,3})(\d{3,4})(\d{3,4})$/);
  if (f) return [f[1], f[2], f[3], f[4]].filter(Boolean).join(' ');
  return tel;
}

export function ContactDetailMobile({ kind, id }: { kind: string; id: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteContatti(user);
  const { data: c, isLoading, isError } = useQuery({ ...contattoQuery(kind, id), retry: false });
  const { data: note } = useQuery({
    queryKey: ['client-notes', id],
    queryFn: () => api.get<Array<{ id: string; body: string; pinned: boolean; createdAt: string; author: { firstName: string; lastName: string } | null }>>(`/clients/${id}/notes`),
    enabled: kind === 'client',
  });

  const promuovi = useMutation({
    mutationFn: () => api.post<{ key: string; trattativeCollegate?: number }>('/contacts/promuovi', { kind, id }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['deals'] });
      toast.ok('Ora è un cliente', r.trattativeCollegate ? `${r.trattativeCollegate} trattative collegate alla scheda` : undefined);
      navigate(`/contatti/${r.key}`, { replace: true });
    },
    onError: (e: any) => toast.alert('Non promosso', e?.message ?? 'Riprova'),
  });

  if (isError) return <MPage><MEmpty>Contatto non trovato: forse è stato eliminato, o il link è sbagliato.</MEmpty></MPage>;
  if (isLoading || !c) return <MPage><MEmpty>Carico la scheda…</MEmpty></MPage>;

  const a = c.anagrafica;
  const nome = leggibile(c.name);
  // Se l'azienda non ha un recapito suo ma un referente sì, si usa quello.
  const ripiego = c.referenti.find((r) => r.isPrimary && (r.phone || r.email)) ?? c.referenti.find((r) => r.phone || r.email) ?? null;
  const telefono = c.recapiti.phone ?? ripiego?.phone ?? null;
  const email = c.recapiti.email ?? ripiego?.email ?? null;
  const wa = numeroWhatsApp(telefono);
  const indirizzo = [a.addressLine, a.zip, a.city, a.province].filter(Boolean).join(', ');
  const cosE = c.tipi.map((t) => TIPO_LABEL[t]).join(' e ') || 'Contatto';

  const aperte = c.trattative.filter((t) => t.outcome === 'APERTO');
  const mensile = c.canoneMensile + c.mensileAbbonamenti;
  const f = c.fatture;
  const forn = c.fornitore;
  const potenziale = c.tipi.includes('POTENZIALE');
  // Quanto ci deve in tutto: lo scaduto è a parte nell'API, e chi aveva solo
  // fatture scadute (Emmequadro, 1.000 €) prima non vedeva la riga.
  const aperto = f.daIncassare + f.scaduto;

  const soldi = [
    f.totale > 0 && <MRow key="fatt" icon="invoice" title="Fatturato in tutto" sub={`${f.count} ${f.count === 1 ? 'fattura' : 'fatture'} · € ${fmtEur(f.incassatoYtd)} nel ${new Date().getFullYear()}`} subWrap trail={`€ ${fmtEur(f.totale)}`} />,
    aperto > 0 && (
      <MRow
        key="inc"
        icon="clock"
        tone={f.scaduto > 0 ? 'alert' : undefined}
        title="Da incassare"
        sub={f.scaduto > 0 ? <span className="is-alert">€ {fmtEur(f.scaduto)} già scaduti</span> : 'niente di scaduto'}
        trail={`€ ${fmtEur(aperto)}`}
      />
    ),
    mensile > 0 && <MRow key="mese" icon="repeat" title="Ogni mese" sub="canoni in corso" trail={`€ ${fmtEur(mensile)}`} />,
    c.potenziale > 0 && <MRow key="pot" icon="handshake" title="In trattativa" sub={`${aperte.length} ${aperte.length === 1 ? 'trattativa aperta' : 'trattative aperte'}`} trail={`€ ${fmtEur(c.potenziale)}`} />,
    forn && forn.totale > 0 && <MRow key="forn" icon="receipt" title="Pagato a loro" sub={`${forn.count} ${forn.count === 1 ? 'fattura ricevuta' : 'fatture ricevute'}`} trail={`€ ${fmtEur(forn.totale)}`} />,
    forn && forn.daPagare > 0 && <MRow key="dap" icon="calendarClock" tone="alert" title="Da pagare a loro" trail={`€ ${fmtEur(forn.daPagare)}`} />,
  ].filter(Boolean);

  return (
    <>
      <MPage>
        <MIdentity
          text={iniziali(nome)}
          title={nome}
          sub={[cosE, a.city ? leggibile(a.city) : null, a.sector].filter(Boolean).join(' · ')}
        />

        <MActions
          azioni={[
            { label: 'Chiama', icon: 'phone', href: telefono ? `tel:${telefono.replace(/\s+/g, '')}` : null },
            { label: 'Scrivi', icon: 'mail', href: email ? `mailto:${email}` : null },
            { label: 'WhatsApp', icon: 'message', href: wa ? `https://wa.me/${wa}` : null },
            { label: 'Mappa', icon: 'pin', href: indirizzo ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(indirizzo)}` : null },
          ]}
        />

        {(telefono || email || indirizzo) && (
          <MGroup title="Recapiti">
            {telefono && <MRow icon="phone" title={telefonoLeggibile(telefono)} sub={c.recapiti.phone ? 'telefono dell\'azienda' : `di ${ripiego?.fullName}`} />}
            {email && <MRow icon="mail" title={email} sub={c.recapiti.email ? 'email dell\'azienda' : `di ${ripiego?.fullName}`} />}
            {indirizzo && <MRow icon="pin" title={leggibile(a.addressLine ?? a.city ?? '')} sub={[a.zip, a.city ? leggibile(a.city) : null, a.province].filter(Boolean).join(' ')} />}
          </MGroup>
        )}

        {soldi.length > 0 && <MGroup title="Soldi">{soldi}</MGroup>}

        {c.referenti.length > 0 && (
          <MGroup title="Persone" count={c.referenti.length}>
            {c.referenti.map((r) => (
              <MRow
                key={r.id}
                text={iniziali(r.fullName)}
                round
                title={r.fullName}
                sub={[r.role, r.phone ?? r.email].filter(Boolean).join(' · ') || undefined}
                onClick={r.phone || r.email ? () => { window.location.href = r.phone ? `tel:${r.phone.replace(/\s+/g, '')}` : `mailto:${r.email}`; } : undefined}
              />
            ))}
          </MGroup>
        )}

        {aperte.length > 0 && (
          <MGroup title="Trattative aperte" count={aperte.length}>
            {aperte.map((t) => (
              <MRow key={t.id} icon="handshake" title={t.title} sub={`${t.stage}${t.expectedCloseDate ? ` · chiusura prevista il ${fmtDate(t.expectedCloseDate)}` : ''}`} trail={t.amount > 0 ? `€ ${fmtEur(t.amount)}` : undefined} onClick={() => navigate(`/trattative/${t.id}`)} />
            ))}
          </MGroup>
        )}

        {c.progetti.length > 0 && (
          <MGroup title="Progetti" count={c.progetti.length}>
            {c.progetti.map((p) => (
              <MRow key={p.id} icon="cube" title={p.name.split(/\s+[—–-]\s+/).slice(1).join(' ') || p.name} sub={`${p.code} · ${p.progress}% fatto`} onClick={() => navigate(`/projects/${p.id}`)} />
            ))}
          </MGroup>
        )}

        {c.ultimeFatture.length > 0 && (
          <MGroup title="Ultime fatture">
            {c.ultimeFatture.slice(0, 4).map((x) => (
              <MRow
                key={x.id}
                icon="invoice"
                title={x.number ?? 'Senza numero'}
                sub={`del ${fmtDate(x.issueDate)}${x.status === 'PAGATA' ? ' · pagata' : x.dueDate ? ` · scade il ${fmtDate(x.dueDate)}` : ''}`}
                trail={`€ ${fmtEur(x.total)}`}
                onClick={() => navigate(`/invoices/${x.id}`)}
              />
            ))}
          </MGroup>
        )}

        {/* Le note del cliente: sul telefono la "scheda estesa" è questa stessa
            pagina (ClientDetailPage la apre così), quindi le note stanno qui.
            Documenti, margini e diario completo restano al computer. */}
        {c.kind === 'client' && (
          <MGroup
            title="Note"
            count={note?.length || undefined}
            action={canWrite ? { label: 'Nuova nota', onClick: () => useModalStore.getState().open('newClientNote', { clientId: c.id }) } : undefined}
            note={(note?.length ?? 0) > 3 ? 'Qui le ultime tre: il diario completo è nella scheda del computer.' : undefined}
          >
            {!note?.length && <MEmpty>Nessuna nota sul cliente.</MEmpty>}
            {[...(note ?? [])].sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.createdAt.localeCompare(a.createdAt)).slice(0, 3).map((n) => (
              <MRow
                key={n.id}
                icon={n.pinned ? 'pin' : 'pencil'}
                title={`${fmtDate(n.createdAt)} · ${n.author ? `${n.author.firstName} ${n.author.lastName}` : 'Sistema'}`}
                sub={n.body}
                subWrap
              />
            ))}
          </MGroup>
        )}
      </MPage>

      {canWrite && potenziale && (
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" disabled={promuovi.isPending} onClick={() => promuovi.mutate()}>
            <Icon name="check" /> Diventa cliente
          </button>
        </div>
      )}
    </>
  );
}
